// =============================================================================
// DmSidebar — replaces the channel sidebar when in DM mode
// =============================================================================
// Lists the user's existing DM threads (sorted by recent activity) plus a
// "New DM" button that opens the user-search modal. Click a thread to
// navigate to /dm/<channelId> and load its messages.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useDms, useUnreads, useFriends } from '../hooks';
import { api, ApiError } from '../api';
import { Avatar, Button, Icon, Input, Modal, Spinner } from './ui';
import { NewMeetingModal } from './NewMeetingModal';
import type { DmThread, UserSearchResult } from '../types';

interface DmSidebarProps {
  activeChannelId: string | null;
  /**
   * Reveal the main pane on mobile. Needed for entries whose route sets
   * neither `channelSlug` nor `dmChannelId` — AppShell's pane effect keys off
   * those two params, so /friends would otherwise navigate with the main pane
   * still hidden and nothing would appear. DM threads don't need this (their
   * route sets dmChannelId, which the effect already handles).
   */
  onNavigateMain?: () => void;
}

export function DmSidebar({ activeChannelId, onNavigateMain }: DmSidebarProps) {
  const { threads, loading, refresh } = useDms();
  const { unreadByChannel } = useUnreads();
  const { incomingCount } = useFriends();
  const navigate = useNavigate();
  const location = useLocation();
  const friendsActive = location.pathname.startsWith('/friends');
  const [newDmOpen, setNewDmOpen] = useState(false);

  const [meetingOpen, setMeetingOpen] = useState(false);
  return (
    <>
      {/* Friends entry — top of the home area. */}
      <button
        onClick={() => {
          navigate('/friends');
          onNavigateMain?.();
        }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-border-soft ${
          friendsActive ? 'bg-accent text-accent-text' : 'text-text hover:bg-elevated/50'
        }`}
      >
        <Icon name="user" size={16} />
        <span className="text-sm font-semibold flex-1">Friends</span>
        {incomingCount > 0 && (
          <span
            className="bg-danger text-white text-[9px] font-bold px-1.5 rounded-full leading-[1.4]"
            title={`${incomingCount} pending request${incomingCount === 1 ? '' : 's'}`}
          >
            {incomingCount}
          </span>
        )}
      </button>

      <div className="px-3 py-2.5 border-b border-border bg-elevated/50 flex items-center justify-between gap-1">
        <div className="font-bold text-text truncate flex-1">Direct Messages</div>
        {/* Instant meeting rooms live here rather than only on the empty DM
            home screen: that screen is only reachable with no thread selected,
            which made the feature effectively undiscoverable. This header is
            visible the whole time you're in the DM section. */}
        <button
          onClick={() => setMeetingOpen(true)}
          className="aim-button p-1.5! shadow-none!"
          title="Start a meeting — get a link anyone can join, no account needed"
          aria-label="Start a meeting"
        >
          <Icon name="link" size={12} />
        </button>
        <button
          onClick={() => setNewDmOpen(true)}
          className="aim-button p-1.5! shadow-none!"
          title="New DM"
          aria-label="New DM"
        >
          <Icon name="plus" size={12} />
        </button>
      </div>
      <NewMeetingModal open={meetingOpen} onClose={() => setMeetingOpen(false)} />

      <div className="flex-1 min-h-0 overflow-y-auto bg-bg">
        {loading ? (
          <div className="p-4 text-center text-text-subtle text-xs">
            <Spinner size={14} /> Loading…
          </div>
        ) : threads.length === 0 ? (
          <div className="p-4 text-center text-text-subtle text-xs">
            No DMs yet. Click + to start one.
          </div>
        ) : (
          <div className="py-1">
            {threads.map((t) => (
              <DmThreadRow
                key={t.channelId}
                thread={t}
                active={t.channelId === activeChannelId}
                unread={unreadByChannel[t.channelId]}
                onClick={() => navigate(`/dm/${t.channelId}`)}
              />
            ))}
          </div>
        )}
      </div>

      <NewDmModal
        open={newDmOpen}
        onClose={() => setNewDmOpen(false)}
        onCreated={(thread) => {
          setNewDmOpen(false);
          refresh();
          navigate(`/dm/${thread.channelId}`);
        }}
      />
    </>
  );
}

function DmThreadRow({
  thread,
  active,
  unread,
  onClick,
}: {
  thread: DmThread;
  active: boolean;
  unread?: { unreadCount: number; mentionCount: number };
  onClick: () => void;
}) {
  const name = thread.otherUser.displayName || thread.otherUser.username;
  const showUnread = !active && unread && unread.unreadCount > 0;
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
        active
          ? 'bg-accent text-accent-text'
          : showUnread
            ? 'text-text font-semibold hover:bg-elevated/50'
            : 'text-text hover:bg-elevated/50'
      }`}
    >
      <Avatar name={name} url={thread.otherUser.avatarUrl} size={28} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        <div className={`text-[10px] truncate ${active ? 'opacity-70' : 'text-text-subtle'}`}>
          @{thread.otherUser.username}
        </div>
      </div>
      {showUnread && (
        <span
          className="bg-danger text-white text-[9px] font-bold px-1.5 rounded-full leading-[1.4]"
          title={`${unread!.unreadCount} unread`}
        >
          {unread!.unreadCount}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// NewDmModal — pick a user to start a DM with
// =============================================================================
function NewDmModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (thread: DmThread) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced search — re-run 200ms after the user stops typing
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const found = await api.searchUsers(q);
        setResults(found);
      } catch {
        // ignore — surface only on action errors
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  async function handlePick(user: UserSearchResult) {
    setCreating(true);
    setError(null);
    try {
      const thread = await api.openDm({ userId: user.id });
      onCreated(thread);
      // Reset for next open
      setQuery('');
      setResults([]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to open DM');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New direct message" width="w-[420px]">
      <div className="space-y-3">
        <Input
          autoFocus
          label="Find someone by username"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Start typing a username…"
        />

        <div className="bevel-down rounded-aim bg-elevated min-h-[120px] max-h-[280px] overflow-y-auto">
          {searching && results.length === 0 ? (
            <div className="p-4 text-center text-text-subtle text-xs">
              <Spinner size={14} /> Searching…
            </div>
          ) : !query.trim() ? (
            <div className="p-4 text-center text-text-subtle text-xs">
              Type at least one letter to search.
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-text-subtle text-xs">
              No users found.
            </div>
          ) : (
            <div className="py-1">
              {results.map((u) => {
                const name = u.displayName || u.username;
                return (
                  <button
                    key={u.id}
                    onClick={() => handlePick(u)}
                    disabled={creating}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-bg disabled:opacity-50"
                  >
                    <Avatar name={name} url={u.avatarUrl} size={24} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div className="text-[10px] text-text-subtle truncate">@{u.username}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && <div className="text-xs text-danger">{error}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}
